import { useState, useEffect, useRef } from "react";
import ErrorBoundary from "../../../../../../../helper/shared/mapErrorBoundary";
import SkillMap from "../index";
import { MapsTooltip } from "@syncfusion/ej2-maps";
import { logError } from "../../../../../../../utils/AppInsights";

// --- Monkey patch tooltip errors ---
const originalRenderTooltip = MapsTooltip.prototype.renderTooltip;
MapsTooltip.prototype.renderTooltip = function (...args) {
  try {
    return originalRenderTooltip.apply(this, args);
  } catch (err) {
    logError("Suppressed Syncfusion tooltip error: " + err);
    return null;
  }
};
// ----------------------------------

const SkillMapWrapper = ({ chartsData }) => {
  const [retryKey, setRetryKey] = useState(0);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const locationCache = useRef(new Map());
  const abortController = useRef(null);

  useEffect(() => {
    // 💡 Cancel previous fetch as soon as chartsData changes
    if (abortController.current) {
      abortController.current.abort();
    }

    if (!chartsData || chartsData.length === 0) {
      setLocations([]);
      setLoading(false);
      return;
    }

    const locationSet = new Set();
    const distinctLocations = [];

    chartsData.forEach((entry) => {
      entry.skills.forEach((skill) => {
        skill.location.forEach((loc) => {
          const key = `${loc.lat},${loc.long}`;
          if (!locationSet.has(key)) {
            locationSet.add(key);
            distinctLocations.push({
              latitude: loc.lat,
              longitude: loc.long,
              tooltipText: `Lat: ${loc.lat}, Lon: ${loc.long}`, // fallback
            });
          }
        });
      });
    });

    abortController.current = new AbortController();
    fetchLocations(distinctLocations, abortController.current.signal);

    return () => {
      // cleanup if component unmounts
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [chartsData]);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchLocations = async (distinctLocations, signal) => {
    try {
      setLoading(true);
      const updatedLocations = [];

      for (let i = 0; i < distinctLocations.length; i++) {
        if (signal.aborted) return;

        const loc = distinctLocations[i];
        const key = `${loc.latitude},${loc.longitude}`;

        let locationName;

        if (locationCache.current.has(key)) {
          locationName = locationCache.current.get(key);
        } else {
          locationName = await getLocationName(
            loc.latitude,
            loc.longitude,
            signal
          );
          if (signal.aborted) return;
          locationCache.current.set(key, locationName);

          if (i < distinctLocations.length - 1) {
            await delay(1000); // throttle API calls
          }
        }

        updatedLocations.push({
          latitude: loc.latitude,
          longitude: loc.longitude,
          tooltipText:
            locationName || `Lat: ${loc.latitude}, Lon: ${loc.longitude}`,
        });
      }

      if (!signal.aborted) {
        setLocations(updatedLocations);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching locations:", error);
        setLocations([]);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  async function getLocationName(lat, lon, signal) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
        { signal }
      );
      if (!res.ok) return null;
      const data = await res.json();
      return (
        data?.address?.city || data?.address?.town || data?.address?.country
      );
    } catch (err) {
      if (err.name === "AbortError") return null;
      return null;
    }
  }

  // sanitize before sending to Syncfusion
  const safeLocations = (locations || [])
    .filter(
      (l) =>
        l && typeof l.latitude === "number" && typeof l.longitude === "number"
    )
    .map((l) => ({
      ...l,
      tooltipText:
        typeof l.tooltipText === "string" && l.tooltipText.trim()
          ? l.tooltipText
          : `Lat: ${l.latitude}, Lon: ${l.longitude}`,
    }));

  return (
    <ErrorBoundary onRetry={() => setRetryKey((prev) => prev + 1)}>
      {loading ? (
        <div className="chart-display-width">
          <h3>Users Geolocation</h3>
          <div className="loader-map">
            <div className="loader-wrap">
              <div className="loader-dot loader-dot1"></div>
              <div className="loader-dot loader-dot2"></div>
              <div className="loader-dot loader-dot3"></div>
              <div className="loader-dot loader-dot4"></div>
              <div className="loader-text loader-map-text-display">
                Loading locations...
              </div>
            </div>
          </div>
        </div>
      ) : safeLocations.length > 0 ? (
        <SkillMap key={retryKey} locations={safeLocations} />
      ) : (
        <div className="chart-display-width">
          <h3>Users Geolocation</h3>
          <div className="container-no-records">No Records Found</div>
        </div>
      )}
    </ErrorBoundary>
  );
};

export default SkillMapWrapper;
